<script setup lang="ts">
import {watch, ref} from "vue";

export type StatusMessage = {
  icon?: string;
  type: "error" | "warning" | "success" | "info";
  message: string;
};

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: "",
  },
  label: {
    type: String,
  },
  placeholder: {
    type: String,
    default: "",
  },
  statusMessage: {
    type: Object as () => StatusMessage | undefined,
  },
  required: {
    type: Boolean,
    default: false,
  }
});
const emit = defineEmits(["update:modelValue", "change"]);

const isFocused = ref<boolean>(false);
const input = ref<string>(props.modelValue ?? "");

const oninput = (): void => {
  emit("update:modelValue", input.value);
};

const onChange = (): void => {
  emit("change", input.value);
};

watch(() => props.modelValue, (newValue: string) => {
  input.value = newValue;
});
</script>

<template>
  <div :class="['ui-input', props.statusMessage?.type]">
    <label v-if="props.label" :for="props.id">{{ props.label }}</label>
    <input :required="props.required" type="text" :id="props.id" :placeholder="props.placeholder" v-model="input" @input="oninput" @change="onChange" @focusin="isFocused = true" @blur="isFocused = false" />
    <span class="status-message" v-if="props.statusMessage">
      <Icon v-if="props.statusMessage.icon" :name="props.statusMessage.icon" />
      {{ props.statusMessage.message || "" }}
    </span>
  </div>
</template>

<style scoped lang="scss">
.ui-input {
  display: flex;
  flex-direction: column;
  gap: 10px;

  label {
    color: rgb(var(--input-label-color));
    font-weight: 500;
    font-size: 15px;
    cursor: pointer;
  }

  input {
    padding: 10px 15px;
    background: rgb(var(--input-background));
    border: 1px solid rgba(var(--input-outline-color), 0);
    border-radius: var(--small-border-radius);
    color: rgb(var(--input-text-color));
    font-size: 18px;
    transition: 0.2s;

    &::placeholder {
      color: rgba(var(--input-text-color), 0.4);
    }

    &:focus {
      border-color: rgba(var(--input-active-outline-color), 0.3);
      outline: none;
    }
  }

  .status-message {
    font-size: 15px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  &.error {
    input {
      border-color: rgba(var(--error-color), 0.3);
    }

    .status-message {
      color: rgb(var(--error-color));
    }
  }

  &.warning {
    input {
      border-color: rgba(var(--warning-color), 0.3);
    }

    .status-message {
      color: rgb(var(--warning-color));
    }
  }

  &.success {
    input {
      border-color: rgba(var(--success-color), 0.3);
    }

    .status-message {
      color: rgb(var(--success-color));
    }
  }

  &.info {
    input {
      border-color: rgba(var(--info-color), 0.3);
    }

    .status-message {
      color: rgb(var(--info-color));
    }
  }
}
</style>